import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import GeoJSON from 'ol/format/GeoJSON';
import { Feature } from 'ol';
import KML from 'ol/format/KML';
import { EndpointsService } from './endpoints.service';
import { Layer } from '../menu/view-menu/layer';
import { cloneDeep } from 'lodash';
import { MapService } from './map.service';
import Point from 'ol/geom/Point';
import LineString from 'ol/geom/LineString';
import { Coordinate } from 'ol/coordinate';
import * as olProj from 'ol/proj';
import Polygon from 'ol/geom/Polygon';
import { ConfigurationLayerModel } from '../models/configuration';
import { Icon, Stroke, Style } from 'ol/style';
import { Color } from 'ol/color';
import { StyleFunction } from 'ol/style/Style';
import { Geometry } from 'ol/geom';
import { lastValueFrom, map } from 'rxjs';
import { CalculationProgressModel } from '../models/calculationProgressModel';
import { v4 as uuidv4 } from 'uuid';
import { ModalControlService } from './modal-control.service';
import { ProgressService } from './progress.service';
import { GlobalSelectionControlService } from '../map/services/global-selection-control.service';
import { PointsService } from './points.service';
import {GritModel} from "../models/grit/grit.model";


@Injectable({
  providedIn: 'root'
})
export class ExportDataService{

  private static readonly KML_ICON_SCALE: number = 0.4;
  public static readonly KML_NAVAID_NAME_PROPERTY: string = "Navaid Name";
  public static readonly KML_CP_NAME_PROPERTY: string = "Coordinate Point Name";

  constructor(private http: HttpClient,
    private endpointService: EndpointsService,
    private mapService: MapService,
    private progressService: ProgressService,
    private modalService: ModalControlService,
    private globalSelectionControlService: GlobalSelectionControlService,
    private pointsService: PointsService
    ){}

  //Function to encode an image file to Base64
  async encodeImageToBase64(imagePath): Promise<string> {
    const buffer: ArrayBuffer = await lastValueFrom(this.http.get(imagePath, { responseType: 'arraybuffer' }));
    // Convert the ArrayBuffer to a Base64 string
    const base64String = btoa(String.fromCharCode.apply(null, new Uint8Array(buffer)));
    return base64String;
  }

  public async getEmbeddedIconStyle(style: Style, urlToBase64Map?: Map<string, string>): Promise<Style> {
    if (style == null){
      return style;
    }

    const icon : Icon = (style.getImage() as Icon);
    if (icon == null){
      return style;
    }

    const srcImgUrl: string = icon.getSrc();
    if (srcImgUrl == null || srcImgUrl == ""){
      return style;
    }

    let base64Image: string;

    if (urlToBase64Map != null){
      if (!urlToBase64Map.has(srcImgUrl)){
        urlToBase64Map.set(srcImgUrl, await this.encodeImageToBase64(srcImgUrl));
      }
      base64Image = `<![CDATA[data:image/png;base64,${urlToBase64Map.get(srcImgUrl)}]]>`;
    } else {
      base64Image = `<![CDATA[data:image/png;base64,${ await this.encodeImageToBase64(srcImgUrl)}]]>`;
    }

    const styleToReturn =  new Style({
      image: new Icon({
        scale: ExportDataService.KML_ICON_SCALE,
        color: icon.getColor(),
        src: base64Image,
      }),
    });

    return styleToReturn;
  }

  private startKmlProgress(layers: Layer[]): [string, number]{ // guid, featureCount
    //count features and start progress
    let progressFeaturesCount: number = 0;
    for (const layer of layers) {
      progressFeaturesCount += layer.olLayer.getSource().getFeatures().length;
    }
    // Generate a new GUID
    const guid: string = uuidv4();
    // create progress object
    const progressObject: CalculationProgressModel = new CalculationProgressModel();
    progressObject.loadedPercentage = 0;
    progressObject.message = "Writing features to KML file...";
    progressObject.calculationType = "Export Data";
    // create new ongoing calculation progress
    this.progressService.progressMap.set(guid, progressObject);
    // return guid
    return [guid, progressFeaturesCount];
  }


  async exportDataToKML(layers: Layer[],userlayer:boolean=false){
    let features = []
    const guid_allFeaturesLength: [string, number] = this.startKmlProgress(layers);
    const progressGuid: string = guid_allFeaturesLength[0];
    const allFeaturesLength: number = guid_allFeaturesLength[1];
    const urlToBase64Map: Map<string, string> = new Map();
    let featureCount: number = 0;
    let progress: number = 0;
    const selectedFeatures = cloneDeep(this.pointsService.getSelectedFeatures());
    try {
      for (const layer of layers) {
        const feats: Feature<Geometry>[] = layer.olLayer.getSource().getFeatures();
        // Deep copy the array of features
        const deepCopyFeats: Feature<Geometry>[] = cloneDeep(feats);

        for (let i = 0; i<deepCopyFeats.length; i++){
          const feature = deepCopyFeats[i];
          // change label of feature in google earth.
          // google earth reads the label from the name property of the feature.
          // create new properties "navaidName" or "coordinatePointName" to save the original name.
          if (feature.get("navaidid") !== undefined){
            feature.set(ExportDataService.KML_NAVAID_NAME_PROPERTY, feature.get("name"));
            // replace name property with navaidId
            feature.set("name", feature.get("navaidid"));
          }
          else if (feature.get("coordpointid") !== undefined){
            feature.set(ExportDataService.KML_CP_NAME_PROPERTY, feature.get("name"));
            // replace name property with coordpointid
            feature.set("name", feature.get("coordpointid"));
          }

          let isFeatureSelected: boolean = false;
          // update ongoing calculation progress parameteres
          featureCount++;
          const onGoingProgress: CalculationProgressModel = this.progressService.progressMap.get(progressGuid);
          onGoingProgress.message = `Writing feature ${featureCount}/${allFeaturesLength} to KML file... `;
          progress += 100 / allFeaturesLength;
          onGoingProgress.loadedPercentage = Math.round(progress);
          this.progressService.cdRefCalcResultsMenu.detectChanges();
          try {
            if (selectedFeatures.findIndex(f => f.get("id") == feature.get("id")) != -1){
              const selectedFeature: Feature<Geometry> = feats[i];
              this.globalSelectionControlService.removeSelectedFeature(selectedFeature);
              isFeatureSelected = true;
              feature.setStyle(cloneDeep(selectedFeature).getStyle());
            }
            try{

              if (feature.getStyle() == null){
                if (typeof layer.olLayer.getStyle() === 'function') {
                  feature.setStyle(await this.getEmbeddedIconStyle((layer.olLayer.getStyle() as StyleFunction).apply(layer.olLayer.getStyle())[0] as Style, urlToBase64Map));
                } else {
                  feature.setStyle(await this.getEmbeddedIconStyle(layer.olLayer.getStyle() as Style, urlToBase64Map));
                }
              }
              else {
                if (typeof feature.getStyle() === 'function') {
                  feature.setStyle(await this.getEmbeddedIconStyle((feature.getStyle() as StyleFunction).apply(feature.getStyle())[0] as Style, urlToBase64Map));
                } else {
                  feature.setStyle(await this.getEmbeddedIconStyle(feature.getStyle() as Style, urlToBase64Map));
                }

              }
            }catch{

            }
          } finally {
            if (isFeatureSelected){
              const selectedFeature = feats[i];
              this.globalSelectionControlService.setSelectedFeature(layer.olLayer, selectedFeature);
            }
          }
        }
        features.push(...deepCopyFeats);
      }
      let clonedFeatures = []
      features.forEach((feature:Feature)=> clonedFeatures.push(feature.clone()))

      let kmlFormat = new KML();
      this.setFeatureCoordinates(clonedFeatures);
      let kml = kmlFormat.writeFeatures(clonedFeatures);

      // Replace XML entities with respective characters
      if(!userlayer)
        kml = kml.replace(/&lt;/g, '<').replace(/&gt;/g, '>');

      var downloadLink = document.createElement('a');
      downloadLink.href = 'data:application/vnd.google-earth.kml+xml;charset=utf-8,' + encodeURIComponent(kml);
      downloadLink.download = 'features.kml';
      downloadLink.click();
    } catch (e) {
      this.modalService.showMesssageModal("Error Exporting Data", "The was an error Exporting Data.");
    } finally {
      this.progressService.progressMap.delete(progressGuid);
    }
  }

  setFeatureCoordinates(features: Feature[]){
    features.forEach((feature:Feature) =>{

      if(feature.get('geometry') instanceof Point){
        //feature.setProperties({geometry: new Point(olProj.fromLonLat([feature.get('longitude'), feature.get('latitude')], this.mapService.getMaps().getView().getProjection()))})
        let coordinates = feature.get('geometry').getCoordinates();
        feature.setProperties({geometry: new Point(olProj.toLonLat(coordinates, this.mapService.getMaps().getView().getProjection()))})
      }
      else if(feature.get('geometry') instanceof LineString){

        const coordinates:Coordinate[] = (feature.get('geometry') as LineString).getCoordinates();
        let newCoordinates: Coordinate[] = [];
        coordinates.forEach((coordinate: Coordinate) =>{
          newCoordinates.push(olProj.toLonLat(coordinate,this.mapService.getMaps().getView().getProjection()));
        });
        feature.setProperties({geometry: new LineString(newCoordinates)});
      }
      else if(feature.get('geometry') instanceof Polygon){

        const coordinates:Coordinate[] = (feature.get('geometry') as Polygon).getCoordinates()[0];
        let newCoordinates: Coordinate[] = [];
        coordinates.forEach((coordinate: Coordinate) =>{
          newCoordinates.push(olProj.toLonLat(coordinate,this.mapService.getMaps().getView().getProjection()));
        });
        feature.setProperties({geometry: new Polygon([newCoordinates])});
      }

    })
  }
  // exportStaticLayerDataToShapeFile(layers: ConfigurationLayerModel[]){
  //   let features = []
  //   layers.forEach(layer => {
  //     features.push(layer.tileLayer.tile);
  //   })
  //   let clonedFeatures = []
  //   features.forEach((feature:Feature)=> clonedFeatures.push(feature.clone()))
  //   var geojsonFormat = new GeoJSON();

  //   clonedFeatures.forEach((feature :Feature)=>{
  //     let featureProperties = feature.getProperties()
  //     for (const key in featureProperties) {
  //       if (Array.isArray(featureProperties[key])) {
  //         featureProperties[key] = null;
  //       }
  //     }
  //     feature.setProperties(featureProperties);
  //   })
  //   this.setFeatureCoordinates(clonedFeatures);
  //   /**
  //    * feature.setProperties({actualScreeningAngle: null,associatedProcedures:null
  //         ,associatedProceduresCritical:null,
  //         sectorizations:null
  //       })
  //    */
  //   var geojson = geojsonFormat.writeFeaturesObject(clonedFeatures);
  //   this.http.post(
  //     this.endpointService.getShapeFileFromGeoJson,
  //     geojson,
  //     this.endpointService.fileHttpOptions
  //   ).subscribe((blob:Blob) => {

  //     const fileName: string = 'shapefiles.zip';
  //     const objectUrl: string = URL.createObjectURL(blob);
  //     const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;

  //     a.href = objectUrl;
  //     a.download = fileName;
  //     document.body.appendChild(a);
  //     a.click();

  //     document.body.removeChild(a);
  //     console.log();
  //   })
  // }
  exportDataToShapeFile(layers: Layer[]){
    let features = []
    layers.forEach(layer => {
      const feats: Feature<Geometry>[] = layer.olLayer.getSource().getFeatures();
      for (const feature of feats){
        if (feature.getStyle() == null){
          feature.setStyle(layer.olLayer.getStyleSelected());
        }
      }
      features.push(...feats);
    })
    let clonedFeatures = []
    features.forEach((feature:Feature)=> clonedFeatures.push(feature.clone()))
    var geojsonFormat = new GeoJSON();

    clonedFeatures.forEach((feature :Feature)=>{
      let featureProperties = feature.getProperties()
      for (const key in featureProperties) {
        if (Array.isArray(featureProperties[key]) || key == "layer") {
          featureProperties[key] = null;
        }else if (key == "entity"){
          featureProperties[key] = JSON.stringify((featureProperties[key] as GritModel<any>).toDTO());
        }
      }
      let color: number[] = null;
      if(feature.get('geometry') instanceof LineString){
        if((feature.getStyle() as Style).getStroke != null){
          color = (feature.getStyle() as Style).getStroke().getColor() as Color;
        }
        else{
          color = (feature.getStyleFunction().apply(feature.getStyleFunction(), [feature])[0] as Style).getStroke().getColor() as Color;
        }
      }
      else if(feature.get('geometry') instanceof Polygon){
        if((feature.getStyle() as Style).getFill != null){
          color = (feature.getStyle() as Style).getFill().getColor() as Color;
        }
        else{
          color = (feature.getStyleFunction().apply(feature.getStyleFunction(), [feature])[0] as Style).getStroke().getColor() as Color;
        }
      }
      featureProperties['color'] = color;
      feature.setProperties(featureProperties);
    })
    this.setFeatureCoordinates(clonedFeatures);
    /**
     * feature.setProperties({actualScreeningAngle: null,associatedProcedures:null
          ,associatedProceduresCritical:null,
          sectorizations:null
        })
     */
    var geojson = geojsonFormat.writeFeaturesObject(clonedFeatures);
    this.http.post(
      this.endpointService.getShapeFileFromGeoJson,
      geojson,
      this.endpointService.fileHttpOptions
    ).subscribe((blob:Blob) => {

      const fileName: string = 'shapefiles.zip';
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;

      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      document.body.removeChild(a);
      console.log();
    })
  }
}
